import { Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { IState } from '../shared/models/state';
import { ContactsService } from './shared/services/contacts.service';
import {
  getContactsColsSelector,
  getContactsTotalSelector,
  getEventsOptionSelector,
  getRawContactsColumnsSelector,
  getTempPageSelector
} from './contacts.reducer';
import { ContactsReports } from './reports/contacts.report';
import { Column, ContactsReportData, Page } from './shared/models/Contact';
import { saveAs } from 'file-saver';
import * as moment from 'moment';
import { ToggleContactsTableAction } from './contacts.actions';
import { getLimitsSelector } from '../shared/limits/limits.reducer';
import { AccountLimits } from '../account/account.model';
import { NotificationService } from '../shared/notification.service';
import { DatePickerService } from '../shared/services/date.picker.service';

@Component({
  selector: 'cc-contacts',
  templateUrl: './contacts.component.html',
  styleUrls: ['./contacts.component.scss']
})
export class ContactsComponent implements OnInit {
  public tempPageFilters: Page;
  public columns: Array<{ key: string; name: string }>;
  public isEventsTable: boolean;
  public startDate: string | moment.Moment;
  public endDate: string | moment.Moment;
  public utcOffset = moment().utcOffset();
  public maxDate: string | moment.Moment;
  public minDate: string | moment.Moment;
  public datepickerVisible: boolean;
  public opens: string;
  public rawColumns: Column[];
  public phrase: string;
  public minReportDate: string;
  public hasDataToExport = false;
  private defaultFilterRange = {
    start: moment().subtract(7, 'day').startOf('day').add(this.utcOffset / 60, 'hours').toISOString(),
    end:   moment().endOf('day').add(this.utcOffset / 60, 'hours').toISOString()
  };

  constructor(
    private store: Store<IState>,
    public contactsService: ContactsService,
    public datePickerService: DatePickerService,
    public contactsReports: ContactsReports,
    public notificationService: NotificationService
  ) {}

  ngOnInit() {
    this.store.select(getLimitsSelector).subscribe((limits: AccountLimits) => {
      this.minDate = this.contactsService.calculateMinDateByLimit(limits.callsHistoryLimit, this.utcOffset);
      this.contactsService.onChangeRange({
        dateFrom: this.startDate
          ? this.getAcceptableStartDate(this.startDate, this.minDate)
          : this.defaultFilterRange.start,
        dateTo: this.endDate ? this.endDate : this.defaultFilterRange.end
      });
    });

    this.store.select(getTempPageSelector).subscribe((tempPage: Page) => {
      this.tempPageFilters = tempPage;
      if (this.tempPageFilters && tempPage.filter) {
        this.startDate = tempPage.filter.dateFrom
          ? this.getAcceptableStartDate(tempPage.filter.dateFrom, this.minDate)
          : this.minDate;
        this.endDate = tempPage.filter.dateTo;
        this.phrase = tempPage.filter.fullTextSearchPhrase;
      } else {
        this.startDate = this.defaultFilterRange.start;
        this.endDate = this.defaultFilterRange.end;
      }
    });

    this.store.select(getContactsColsSelector).subscribe((columns: Column[]) => {
      this.columns = columns;
    });

    this.store.select(getRawContactsColumnsSelector).subscribe((rawColumns: Column[]) => {
      this.rawColumns = rawColumns;
    });

    this.store.select(getEventsOptionSelector).subscribe((isEventsTable: boolean) => {
      this.isEventsTable = isEventsTable ? isEventsTable : false;
      if (isEventsTable === undefined) {
        this.store.dispatch(new ToggleContactsTableAction(this.isEventsTable));
        this.setDefaultViewToEvents();
      }
    });

    this.datePickerService.onTogglePicker.subscribe((open: { open: string; show: boolean }) => {
      this.opens = open && open.open ? open.open : 'right';
      this.datepickerVisible = open && open.show ? open.show : false;
    });

    this.store.select(getContactsTotalSelector).subscribe((total: number) => {
      this.hasDataToExport = !!total;
    });

    this.contactsService.getPossibleMinDate().subscribe(response => {
      this.minReportDate =
        response && response.earliestReportDate ? moment(response.earliestReportDate).toISOString() : null;
    });

    this.contactsService.closeAllFilters();
    this.maxDate = moment().endOf('day').add(this.utcOffset / 60, 'hours').toISOString();
    this.startDate = this.getAcceptableStartDate(this.startDate, this.minDate);
  }

  getAcceptableStartDate(startDate, minDate) {
    return startDate && minDate ? (moment(startDate).isSameOrAfter(minDate) ? startDate : minDate) : this.minDate;
  }

  createRequestParams(): ContactsReportData {
    return {
      columns: this.rawColumns.map(item => {
        const name = item.name.replace('CONTACTS_LIST.', '');
        return { ...item, name };
      }),
      date: moment().toISOString(),
      filter: { ...this.tempPageFilters.filter },
      sorting: {
        order: this.tempPageFilters.pagination.order,
        sorting: this.tempPageFilters.pagination.sortBy
      },
      timezoneOffsetMinutes: moment().utcOffset()
    };
  }

  getContactsReport() {
    this.contactsReports.getContactsReport(this.createRequestParams()).subscribe(response => {
      const currentDateName = moment()
        .format()
        .split('+')[0]
        .replace('T', '_');
      const fileName = `contacts_${currentDateName}.xlsx`;
      const blob = new Blob([response], { type: 'application/x-ms-excel' });
      saveAs(blob, fileName);
    });
  }

  toggleTable() {
    this.isEventsTable = !this.isEventsTable;
    this.store.dispatch(new ToggleContactsTableAction(this.isEventsTable));
  }

  changeDateRange(event) {
    // from contacts-table...
    if (moment(event.dateFrom).isSameOrAfter(this.minDate)) {
      const data = {
        ...this.tempPageFilters,
        filter: {
          ...this.tempPageFilters.filter,
          dateFrom: moment(event.dateFrom),
          dateTo: moment(event.dateTo),
          contactId: this.tempPageFilters.filter.contactId,
        },
        filters: {
          dateFrom: moment(event.dateFrom).toISOString(),
          dateTo: moment(event.dateTo).toISOString()
        }

      }
      this.contactsService.onChangeRange(event);
      this.contactsService.fetchDataAction(data, 0);
      this.contactsService.rowCollapse();
    } else {
      this.notificationService.open('REPORT_NOT_IN_BACKWARD_DAYS_RANGE');
    }
  }

  setPhrase(phrase: string | null) {
    this.contactsService.searchByPhrase(phrase === '' ? null : phrase);
  }

  searchByPhrase() {
    this.contactsService.fetchDataAction(this.tempPageFilters, 0);
  }

  clearFilterWhenEmpty(phrase: string) {
    if (!phrase) {
      this.searchByPhrase();
    }
  }

  // Cannot use event's table as default view, because all css classes are
  // defined in the contacts-table.component.scss file, and ContactsTableComponent
  // uses ViewEncapsulation.None to propagate these styles to the EventsTableComponent,
  // so ContactsTableComponent (default isEventsTable = false) must be used before
  // EventsTableComponent to ensure all styles are properly loaded.
  private setDefaultViewToEvents() {
    setTimeout(() => {
      this.isEventsTable = true;
      this.store.dispatch(new ToggleContactsTableAction(this.isEventsTable));
    }, 0);
  }
}
